import { LoadingButton } from '@mui/lab';
import { Alert, Box, Stack, TextField } from '@mui/material';
import { FC, SyntheticEvent, useContext, useState } from 'react';
import PasswordInput from '../../components/PasswordInput/PasswordInput';
import PasswordValidator from '../../components/PasswordValidator/PasswordValidator';
import ScreenHeader from '../../components/ScreenHeader/ScreenHeader';
import FacebookAddPasswordContext, { EFacebookAddPasswordUiState } from '../../contexts/FacebookAddPasswordContext';
import GlobalContext from '../../contexts/GlobalContext';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import { validatePassword } from '../../util/validation/passwordValidation';

enum ELocalUiState {
    default,
    loading,
    error,
}

const FacebookAddPasswordForm: FC = () => {
    const { language } = useContext(GlobalContext);
    const { setFacebookAddPasswordUiState, email, savePassword } = useContext(FacebookAddPasswordContext);
    const [localUiState, setLocalUiState] = useState(ELocalUiState.default);
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);

    const checkPasswordIsValid = () => {
        const isValid = validatePassword(password);
        setPasswordIsValid(isValid);
        return isValid;
    };

    const handleSubmitForm = async (event: SyntheticEvent) => {
        event.preventDefault();
        const passwordIsValid = checkPasswordIsValid();
        if (passwordIsValid) {
            setLocalUiState(ELocalUiState.loading);
            try {
                await savePassword(password);
                setFacebookAddPasswordUiState(EFacebookAddPasswordUiState.success);
            } catch {
                setLocalUiState(ELocalUiState.error);
            }
        }
    };

    return (
        <PageLayoutV2>
            <ScreenHeader
                title={language.deprecateFacebook.password.title}
                desc={language.deprecateFacebook.password.desc}
            />
            {localUiState === ELocalUiState.error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {language.error.generic.subheader}
                </Alert>
            )}
            <form>
                <Stack sx={{ my: 2 }}>
                    <TextField
                        label={language.createAccount.form.email}
                        value={email}
                        id="email"
                        size="small"
                        sx={{ mb: 2 }}
                        disabled={true}
                    />
                    <PasswordInput
                        value={password}
                        error={!passwordIsValid}
                        label={language.createAccount.form.password}
                        onChange={(event) => setPassword(event.target.value)}
                        helperText={!passwordIsValid && language.validation.password.invalid}
                        id="password"
                        size="small"
                        sx={{ mb: 3 }}
                    />
                    <Box sx={{ mb: 3 }} data-testid="password-validator">
                        <PasswordValidator password={password} />
                    </Box>
                    <LoadingButton
                        variant="contained"
                        type="submit"
                        size="large"
                        fullWidth
                        disabled={localUiState === ELocalUiState.loading}
                        onClick={handleSubmitForm}
                        loading={localUiState === ELocalUiState.loading}
                    >
                        {language.deprecateFacebook.password.cta}
                    </LoadingButton>
                </Stack>
            </form>
        </PageLayoutV2>
    );
};

export default FacebookAddPasswordForm;
